import { Button, Collapse, Flex, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Colors from "src/themes/colors";

const { Text } = Typography;

interface BulkResultSummaryProps {
  successCount: number;
  failedNumbers: string[];
  failedDetails?: { phoneNumber: string; reason: string | null }[];
  variant: "send" | "schedule";
  onDone: () => void;
  onResend?: () => void;
  resendLoading?: boolean;
}

/**
 * Replaces the modal form after a partial bulk failure.
 * Shows how many succeeded and a scrollable list of the numbers that failed.
 */
const BulkResultSummary = ({
  successCount,
  failedNumbers,
  failedDetails,
  variant,
  onDone,
  onResend,
  resendLoading,
}: BulkResultSummaryProps) => {
  const { t } = useTranslation();
  const failedCount = failedNumbers.length;

  const successText =
    variant === "schedule"
      ? t("notification.bulkResultScheduled", { count: successCount })
      : t("notification.bulkResultSent", { count: successCount });

  const failedText =
    variant === "schedule"
      ? t("notification.bulkResultFailedSchedule", { count: failedCount })
      : t("notification.bulkResultFailedSend", { count: failedCount });

  return (
    <Flex vertical gap={16} style={{ padding: "1.5rem 0" }}>
      <Flex align="center" gap={8}>
        <CheckCircleFilled style={{ color: Colors.Green }} />
        <Text>{successText}</Text>
      </Flex>
      <Flex align="center" gap={8}>
        <CloseCircleFilled style={{ color: Colors.red }} />
        <Text>{failedText}</Text>
      </Flex>

      <Collapse
        defaultActiveKey={["failed"]}
        items={[
          {
            key: "failed",
            label: t("notification.bulkFailedRecipients", { count: failedCount }),
            children: (
              <div style={{ maxHeight: 180, overflowY: "auto" }}>
                {(failedDetails && failedDetails.length
                  ? failedDetails
                  : failedNumbers.map((n) => ({ phoneNumber: n, reason: null }))
                ).map((item, index) => (
                  <div key={`failed-${index}`} style={{ padding: "2px 0" }}>
                    <Text>{item.phoneNumber}</Text>
                    {item.reason && (
                      <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                        {item.reason}
                      </Text>
                    )}
                  </div>
                ))}
              </div>
            ),
          },
        ]}
      />

      <Flex justify="flex-end" gap={8}>
        {failedCount > 0 && onResend && (
          <Button
            style={{ borderRadius: 50 }}
            loading={resendLoading}
            disabled={resendLoading}
            onClick={onResend}
          >
            {t("heading.resendFailed")}
          </Button>
        )}
        <Button type="primary" style={{ borderRadius: 50 }} onClick={onDone}>
          {t("heading.done")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default BulkResultSummary;
